import React, { Suspense, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import MobileTopNav from "@/components/MobileTopNav";
import HeroSection from "@/components/HeroSection";
import EngineHeroSection from "@/components/EngineHeroSection";
import FeaturedEngines from "@/components/FeaturedEngines";
import PartsHeroSection from "@/components/PartsHeroSection";
import FeaturedParts from "@/components/FeaturedParts";
import JobsHeroSection from "@/components/JobsHeroSection";
import FeaturedJobs from "@/components/FeaturedJobs";
import ServicesHeroSection from "@/components/ServicesHeroSection";
import { HomeInstallPrompt } from "@/components/pwa/HomeInstallPrompt";
type AssetType = 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
import { useIsDesktop } from "@/hooks/use-tablet";
import { organizationSchema, websiteSchema, getJsonLdScript } from "@/utils/structuredData";
import MarketplaceListingCardSkeleton from "@/components/marketplace/MarketplaceListingCardSkeleton";
import MobileNewsCarousel from "@/components/news/MobileNewsCarousel";

// Lazy load below-the-fold content for better initial page load
const FeaturesSection = React.lazy(() => import("@/components/FeaturesSection"));
const Footer = React.lazy(() => import("@/components/Footer"));
const FeaturedServices = React.lazy(() => import("@/components/FeaturedServices"));
// Lazy load FeaturedHelicopters for faster LCP
const FeaturedHelicopters = React.lazy(() => import("@/components/FeaturedHelicopters"));

// Skeleton fallback for featured sections
const FeaturedSectionSkeleton = () => (
  <section className="pt-4 pb-8 md:py-20 bg-background">
    <div className="container mx-auto px-4 md:px-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 text-sm font-medium text-primary">
          Premium Marketplace
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
        {[1, 2, 3].map((i) => (
          <MarketplaceListingCardSkeleton key={i} />
        ))}
      </div>
    </div>
  </section>
);

const Index = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  // Derive asset type from URL (pathname or query param) - single source of truth
  const assetType = useMemo((): AssetType => {
    // Check dedicated routes first - use startsWith to handle trailing slashes and refreshes
    if (location.pathname.startsWith('/engines')) return 'engine';
    if (location.pathname.startsWith('/parts-tools')) return 'parts';
    if (location.pathname.startsWith('/jobs')) return 'jobs';
    if (location.pathname.startsWith('/services')) return 'services';
    // Fall back to query param for backward compatibility
    const assetParam = searchParams.get('asset');
    if (assetParam === 'engine') return 'engine';
    if (assetParam === 'parts') return 'parts';
    if (assetParam === 'jobs') return 'jobs';
    if (assetParam === 'services') return 'services';
    return 'helicopter';
  }, [location.pathname, searchParams]);

  const handleAssetTypeChange = (type: AssetType) => {
    // Navigate to dedicated routes
    if (type === 'engine') navigate('/engines');
    else if (type === 'parts') navigate('/parts-tools');
    else if (type === 'jobs') navigate('/jobs');
    else if (type === 'services') navigate('/services');
    else navigate('/');
  };

  const assetTitle = assetType === 'helicopter' 
    ? 'Helicopters' 
    : assetType === 'engine' 
    ? 'Turbine Engines' 
    : assetType === 'jobs' 
    ? 'Aviation Jobs' 
    : assetType === 'services'
    ? 'Aviation Services'
    : 'Parts & Tools';
  const pageDescription = `Discover premium ${assetTitle.toLowerCase()} worldwide on ChopperSwap. Buy, sell, and rent with verified listings, secure document vault, and instant sharing.`;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>ChopperSwap - The Professional Helicopter Exchange | Buy, Sell & Rent {assetTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:url" content="https://chopperswap.com/" />
        <link rel="canonical" href={`https://chopperswap.com/${assetType !== 'helicopter' ? `?asset=${assetType}` : ''}`} />
        <script type="application/ld+json">{getJsonLdScript(organizationSchema)}</script>
        <script type="application/ld+json">{getJsonLdScript(websiteSchema)}</script>
      </Helmet>
      {/* Desktop Navigation */}
      <div className="hidden lg:block">
        <Navigation />
      </div>
      
      {/* Mobile/Tablet Top Navigation with Search */}
      <MobileTopNav 
        assetType={assetType} 
        onAssetTypeChange={handleAssetTypeChange} 
        showSearch={true}
      />
      
      
      {/* Content with proper spacing for mobile top nav + search */}
      <div className="pt-0">
      
        {assetType === 'helicopter' ? (
          <>
            <div data-tutorial-id="hero-cta-cards">
              <HeroSection />
            </div>
            <div data-tutorial-id="featured-listings">
              <Suspense fallback={<FeaturedSectionSkeleton />}>
                <FeaturedHelicopters />
              </Suspense>
            </div>
            {/* Mobile/Tablet: Inline PWA install prompt at bottom */}
            <HomeInstallPrompt />
          </>
        ) : assetType === 'engine' ? (
          <>
            <EngineHeroSection />
            <FeaturedEngines />
            <HomeInstallPrompt />
          </>
        ) : assetType === 'jobs' ? (
          <>
            <JobsHeroSection />
            <FeaturedJobs />
            <HomeInstallPrompt />
          </>
        ) : assetType === 'services' ? (
          <>
            <ServicesHeroSection />
            <Suspense fallback={null}>
              <FeaturedServices />
            </Suspense>
            <HomeInstallPrompt />
          </>
        ) : (
          <>
            <PartsHeroSection />
            <FeaturedParts />
            <HomeInstallPrompt />
          </>
        )}

        {/* Mobile-only: Insights & News entry point */}
        <MobileNewsCarousel />

        {/* Only show FeaturesSection and Footer on desktop (≥1024px) - hidden on mobile/tablet */}
        {isDesktop && (
          <div className="bg-background relative z-10">
            <Suspense fallback={null}>
              <FeaturesSection />
              <Footer />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  );
};

export default Index;
